import { useState, useEffect } from 'react';
import { Card } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Switch } from '../../../../components/ui/Switch';
import { Briefcase, AlertTriangle, Loader2 } from 'lucide-react';
import { settingsApi } from '../../../../services/api/endpoints/settings';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { PortfolioIntegrationConfig } from '../../../../types/settings';
import toast from 'react-hot-toast';

const PORTFOLIO_TYPES = [
  { value: 'stocks', label: 'Actions (Stocks)' },
  { value: 'bonds',  label: 'Obligations (Bonds)' },
  { value: 'funds',  label: 'Fonds de placement' },
];

const DEFAULT_CONFIG: PortfolioIntegrationConfig = {
  enabled: false,
  portfolioTypes: [{ type: 'stocks', enabled: true }, { type: 'bonds', enabled: true }],
  automaticValuation: false,
  valuationFrequency: 'daily',
  currencyConversion: true,
};

export function PortfolioIntegration() {
  const { isAdmin, can } = usePermissions();
  const canManage = can('manage_integrations') || isAdmin;

  const [config, setConfig] = useState<PortfolioIntegrationConfig>(DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await settingsApi.getPortfolioIntegrationSettings();
        if (!res.success || !res.data) throw new Error();
        setConfig({ ...DEFAULT_CONFIG, ...res.data });
      } catch {
        setError('Impossible de charger la configuration du portefeuille.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    if (!canManage) return;
    setIsSaving(true);
    setError(null);
    try {
      const res = await settingsApi.updateIntegrations({ portfolioIntegration: config });
      if (!res.success) throw new Error();
      toast.success('Configuration portefeuille enregistrée');
    } catch {
      setError('Impossible de sauvegarder. Veuillez réessayer.');
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const set = <K extends keyof PortfolioIntegrationConfig>(key: K, value: PortfolioIntegrationConfig[K]) =>
    setConfig(prev => ({ ...prev, [key]: value }));

  const togglePortfolioType = (typeValue: string, enabled: boolean) => {
    const current = config.portfolioTypes || [];
    const exists = current.some(t => (typeof t === 'object' ? t.type : t) === typeValue);
    if (exists) {
      set('portfolioTypes', current.map(t =>
        (typeof t === 'object' ? t.type : t) === typeValue ? { type: typeValue, enabled } : t
      ));
    } else {
      set('portfolioTypes', [...current, { type: typeValue, enabled }]);
    }
  };

  const getTypeEnabled = (typeValue: string): boolean => {
    const pt = (config.portfolioTypes || []).find(t =>
      (typeof t === 'object' ? t.type : t) === typeValue
    );
    return typeof pt === 'object' ? pt.enabled : (pt !== undefined);
  };

  if (isLoading) {
    return (
      <Card title="Gestion de Portefeuille" icon={Briefcase}>
        <div className="flex items-center justify-center h-40 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Chargement...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Gestion de Portefeuille" icon={Briefcase}>
      <div className="space-y-5">
        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-destructive/5 border-destructive/20 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Synchronisez vos actifs financiers (titres, obligations) avec la comptabilité pour une valorisation automatique.
        </p>

        {/* Enable */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Activer la gestion de portefeuille</p>
            <p className="text-xs text-muted-foreground">Valorisation et suivi des actifs financiers</p>
          </div>
          <Switch checked={config.enabled} onCheckedChange={v => set('enabled', v)} disabled={!canManage || isSaving} />
        </div>

        {/* Portfolio types */}
        <div className="border-t border-border pt-4 space-y-1">
          <p className="text-sm font-medium text-foreground mb-2">Types d'actifs gérés</p>
          {PORTFOLIO_TYPES.map(pt => (
            <div key={pt.value} className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-muted/50 transition-colors">
              <p className="text-sm text-foreground">{pt.label}</p>
              <Switch
                checked={getTypeEnabled(pt.value)}
                onCheckedChange={v => togglePortfolioType(pt.value, v)}
                disabled={!canManage || isSaving || !config.enabled}
              />
            </div>
          ))}
        </div>

        {/* Valuation options */}
        <div className="border-t border-border pt-4 grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-muted/50">
            <div>
              <p className="text-sm font-medium text-foreground">Valorisation auto</p>
              <p className="text-xs text-muted-foreground">Recalcul automatique des positions</p>
            </div>
            <Switch
              checked={config.automaticValuation}
              onCheckedChange={v => set('automaticValuation', v)}
              disabled={!canManage || isSaving || !config.enabled}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">Fréquence de valorisation</label>
            <select
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
              value={config.valuationFrequency}
              onChange={e => set('valuationFrequency', e.target.value as any)}
              disabled={!canManage || isSaving || !config.enabled}
            >
              <option value="daily">Quotidienne</option>
              <option value="weekly">Hebdomadaire</option>
              <option value="monthly">Mensuelle</option>
            </select>
          </div>
        </div>

        {/* Currency conversion */}
        <div className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-muted/50">
          <div>
            <p className="text-sm font-medium text-foreground">Conversion de devises</p>
            <p className="text-xs text-muted-foreground">Convertir les actifs en CDF au taux en vigueur</p>
          </div>
          <Switch
            checked={config.currencyConversion}
            onCheckedChange={v => set('currencyConversion', v)}
            disabled={!canManage || isSaving || !config.enabled}
          />
        </div>

        {canManage && (
          <div className="flex justify-end pt-2 border-t border-border">
            <Button onClick={save} disabled={isSaving}>
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Enregistrement...</> : 'Enregistrer'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}